import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from 'reactstrap'
import moment from 'moment'
import qs from 'qs'
import oikosApi from '../../../../services/oikos'
import { notifyError, notifySuccess } from '../../../../utils/notify'

const DEFAULT_FORM_DEF = JSON.stringify(
  [
    {
      id: 's1',
      title: '1 - Identificação',
      icon: 'business',
      fields: [
        {
          name: 'observacoes',
          label: 'Observações',
          type: 'multiline',
          required: false,
        },
      ],
    },
  ],
  null,
  2,
)

const formatDateTime = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '—')

const tryPrettyJson = (raw) => {
  if (raw == null || raw === '') return ''
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw, null, 2)
    } catch {
      return String(raw)
    }
  }
  try {
    return JSON.stringify(JSON.parse(String(raw)), null, 2)
  } catch {
    return String(raw)
  }
}

const sidebarItemStyle = (active) => ({
  cursor: 'pointer',
  padding: '10px 12px',
  borderRadius: 6,
  border: active ? '1px solid rgba(86,100,210,0.45)' : '1px solid transparent',
  background: active ? 'rgba(86,100,210,0.08)' : 'transparent',
  marginBottom: 6,
  transition: 'background 0.15s ease, border 0.15s ease',
})

const ProjDadosTab = ({ project }) => {
  const { user } = useSelector((state) => state.auth)
  const projId = project?.proj_id
  const pfId = user?.pf_id

  const [loadingForms, setLoadingForms] = useState(true)
  const [forms, setForms] = useState([])
  const [selectedFormId, setSelectedFormId] = useState(null)

  const [loadingResps, setLoadingResps] = useState(false)
  const [resps, setResps] = useState([])

  const [editorOpen, setEditorOpen] = useState(false)
  const [editorSaving, setEditorSaving] = useState(false)
  const [editingFormId, setEditingFormId] = useState(null) // null = criar
  const [editNome, setEditNome] = useState('')
  const [editVersao, setEditVersao] = useState('1')
  const [editDef, setEditDef] = useState(DEFAULT_FORM_DEF)
  const [saveChoiceOpen, setSaveChoiceOpen] = useState(false)

  const [respModalOpen, setRespModalOpen] = useState(false)
  const [selectedResp, setSelectedResp] = useState(null)

  const isEditing = editingFormId != null
  const editingOriginal = useMemo(
    () => forms.find((f) => Number(f.projForm_id) === Number(editingFormId)) || null,
    [forms, editingFormId],
  )

  const selectedForm = useMemo(
    () => forms.find((f) => Number(f.projForm_id) === Number(selectedFormId)) || null,
    [forms, selectedFormId],
  )

  const loadForms = useCallback(async () => {
    if (!projId) {
      setForms([])
      setSelectedFormId(null)
      setLoadingForms(false)
      return
    }
    setLoadingForms(true)
    try {
      const res = await oikosApi.get(
        `/?${qs.stringify({
          action: 'get',
          table: 'projForm',
          data: { proj_id: projId, projForm_ativo: 1 },
        })}`,
      )
      const rows = Array.isArray(res?.data) ? res.data : []
      rows.sort((a, b) => Number(b.projForm_id) - Number(a.projForm_id))
      setForms(rows)
      setSelectedFormId((prev) => {
        if (prev && rows.some((r) => Number(r.projForm_id) === Number(prev))) return prev
        return rows[0]?.projForm_id ?? null
      })
    } catch (e) {
      console.error('ProjDadosTab loadForms', e)
      notifyError('Não foi possível carregar os formulários do projeto.')
      setForms([])
      setSelectedFormId(null)
    } finally {
      setLoadingForms(false)
    }
  }, [projId])

  const loadResps = useCallback(async (formId) => {
    if (!formId) {
      setResps([])
      return
    }
    setLoadingResps(true)
    try {
      const res = await oikosApi.get(
        `/?${qs.stringify({
          action: 'get',
          table: 'projFormResp',
          data: { projForm_id: formId, projFormResp_ativo: 1 },
          fields: {
            '(SELECT pf_nomeExibicao FROM pf WHERE pf.pf_id = projFormResp.pf_id LIMIT 1)': 'pf_nome',
          },
        })}`,
      )
      const rows = Array.isArray(res?.data) ? res.data : []
      rows.sort((a, b) => {
        const da = a.projFormResp_dtCria ? moment(a.projFormResp_dtCria).valueOf() : 0
        const db = b.projFormResp_dtCria ? moment(b.projFormResp_dtCria).valueOf() : 0
        return db - da
      })
      setResps(rows)
    } catch (e) {
      console.error('ProjDadosTab loadResps', e)
      notifyError('Não foi possível carregar as respostas.')
      setResps([])
    } finally {
      setLoadingResps(false)
    }
  }, [])

  useEffect(() => {
    loadForms()
  }, [loadForms])

  useEffect(() => {
    loadResps(selectedFormId)
  }, [selectedFormId, loadResps])

  const openCreate = () => {
    setEditingFormId(null)
    setEditNome('')
    setEditVersao('1')
    setEditDef(DEFAULT_FORM_DEF)
    setEditorOpen(true)
  }

  const openEdit = (form) => {
    if (!form?.projForm_id) return
    setEditingFormId(form.projForm_id)
    setEditNome(form.projForm_nome || '')
    setEditVersao(form.projForm_versao || '1')
    setEditDef(tryPrettyJson(form.projForm_def) || DEFAULT_FORM_DEF)
    setEditorOpen(true)
  }

  const closeEditor = () => {
    if (editorSaving) return
    setSaveChoiceOpen(false)
    setEditorOpen(false)
    setEditingFormId(null)
  }

  const parseEditorPayload = () => {
    const nome = String(editNome || '').trim()
    if (!nome) {
      notifyError('Informe o nome do formulário.')
      return null
    }
    if (!projId) {
      notifyError('Projeto inválido.')
      return null
    }
    let defParsed
    try {
      defParsed = JSON.parse(editDef)
    } catch {
      notifyError('O JSON da definição do formulário é inválido.')
      return null
    }
    if (!Array.isArray(defParsed)) {
      notifyError('A definição deve ser um array JSON de seções.')
      return null
    }
    const versao = String(editVersao || '1').trim() || '1'
    return { nome, defParsed, versao, defStr: JSON.stringify(defParsed) }
  }

  /** Criação direta; na edição abre confirmação (atualizar vs nova versão). */
  const requestSaveForm = () => {
    const payload = parseEditorPayload()
    if (!payload) return
    if (isEditing) {
      setSaveChoiceOpen(true)
      return
    }
    handleSaveForm('create', payload)
  }

  /**
   * @param {'create'|'update'|'new_version'} mode
   */
  const handleSaveForm = async (mode, preParsed) => {
    const payload = preParsed || parseEditorPayload()
    if (!payload) return
    const { nome, versao, defStr } = payload

    if (mode === 'new_version' && editingOriginal) {
      const currentVer = String(editingOriginal.projForm_versao || '1')
      if (versao === currentVer) {
        notifyError(
          `Para criar uma nova versão, altere o campo Versão (atual: ${currentVer}).`,
        )
        setSaveChoiceOpen(false)
        return
      }
    }

    setEditorSaving(true)
    try {
      const now = moment().format('YYYY-MM-DD HH:mm:ss')

      if (mode === 'update') {
        await oikosApi.post('/', {
          action: 'update',
          table: 'projForm',
          data: {
            projForm_id: editingFormId,
            projForm_nome: nome,
            projForm_versao: versao,
            projForm_def: defStr,
            projForm_usuarioMod: pfId || undefined,
            projForm_dtMod: now,
          },
        })
        notifySuccess('Formulário atualizado (mesma versão / registro).')
        setSaveChoiceOpen(false)
        setEditorOpen(false)
        await loadForms()
        setSelectedFormId(editingFormId)
      } else {
        // create | new_version → insert
        const res = await oikosApi.post('/', {
          action: 'insert',
          table: 'projForm',
          data: {
            proj_id: projId,
            projForm_nome: nome,
            projForm_versao: versao,
            projForm_def: defStr,
            projForm_ativo: 1,
            projForm_usuarioCria: pfId || undefined,
            projForm_usuarioMod: pfId || undefined,
            projForm_dtCria: now,
            projForm_dtMod: now,
          },
        })
        const created = Array.isArray(res?.data) ? res.data[0] : null
        notifySuccess(mode === 'new_version' ? 'Nova versão do formulário criada.' : 'Formulário criado.')
        setSaveChoiceOpen(false)
        setEditorOpen(false)
        await loadForms()
        if (created?.projForm_id) setSelectedFormId(created.projForm_id)
      }
    } catch (e) {
      console.error('ProjDadosTab save form', e)
      notifyError(
        e?.message ||
          (mode === 'update'
            ? 'Falha ao atualizar formulário.'
            : mode === 'new_version'
              ? 'Falha ao criar nova versão.'
              : 'Falha ao criar formulário.'),
      )
    } finally {
      setEditorSaving(false)
    }
  }

  const openResp = (row) => {
    setSelectedResp(row)
    setRespModalOpen(true)
  }

  if (!projId) {
    return <Alert color="warning" className="mb-0">Projeto não carregado.</Alert>
  }

  if (loadingForms) {
    return (
      <div className="d-flex align-items-center" style={{ gap: 10, padding: '24px 0' }}>
        <Spinner size="sm" />
        <span className="text-muted" style={{ fontSize: 13 }}>Carregando formulários…</span>
      </div>
    )
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12, marginBottom: 12 }}>
        <div>
          <h5 className="mb-1" style={{ fontWeight: 700 }}>Dados de coleta</h5>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            Formulários do projeto e respostas enviadas pelo app.
          </p>
        </div>
        <Button color="primary" size="sm" onClick={openCreate}>
          Novo formulário
        </Button>
      </div>

      <Card className="mt-2">
        <CardBody style={{ padding: 0 }}>
          <div className="d-flex" style={{ minHeight: 420 }}>
            {/* Sidebar */}
            <div
              style={{
                width: 280,
                flexShrink: 0,
                borderRight: '1px solid #e9ecef',
                background: '#f8f9fa',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ marginBottom: 10, padding: '0 4px' }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: '#6c757d', letterSpacing: 0.3 }}>
                  FORMULÁRIOS
                </span>
                <span style={{ fontSize: 12, color: '#6c757d' }}>{forms.length}</span>
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {forms.length === 0 ? (
                  <div
                    className="text-muted"
                    style={{
                      fontSize: 13,
                      padding: 12,
                      border: '1px dashed #dee2e6',
                      borderRadius: 6,
                      background: '#fff',
                    }}
                  >
                    Nenhum formulário neste projeto. Clique em <strong>Novo formulário</strong> para começar.
                  </div>
                ) : (
                  forms.map((f) => {
                    const active = Number(f.projForm_id) === Number(selectedFormId)
                    return (
                      <div
                        key={f.projForm_id}
                        role="button"
                        tabIndex={0}
                        style={sidebarItemStyle(active)}
                        onClick={() => setSelectedFormId(f.projForm_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedFormId(f.projForm_id)
                          }
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: '#343a40',
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                          }}
                        >
                          {f.projForm_nome || `Formulário #${f.projForm_id}`}
                        </div>
                        <div className="d-flex justify-content-between" style={{ marginTop: 4, fontSize: 11, color: '#6c757d' }}>
                          <span>v{f.projForm_versao || '1'}</span>
                          <span>#{f.projForm_id}</span>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {/* Main */}
            <div style={{ flex: 1, minWidth: 0, padding: 16 }}>
              {!selectedForm ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: 280, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Selecione um formulário</div>
                  <div>As respostas coletadas aparecerão aqui.</div>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 10, marginBottom: 14 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 16, color: '#343a40' }}>
                        {selectedForm.projForm_nome}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                        Versão {selectedForm.projForm_versao || '1'}
                        {' · '}
                        ID {selectedForm.projForm_id}
                        {selectedForm.projForm_dtCria
                          ? ` · criado em ${formatDateTime(selectedForm.projForm_dtCria)}`
                          : ''}
                      </div>
                    </div>
                    <div className="d-flex" style={{ gap: 8 }}>
                      <Button color="primary" outline size="sm" onClick={() => openEdit(selectedForm)}>
                        Editar formulário
                      </Button>
                      <Button color="secondary" outline size="sm" onClick={() => loadResps(selectedFormId)} disabled={loadingResps}>
                        Atualizar
                      </Button>
                    </div>
                  </div>

                  {loadingResps ? (
                    <div className="d-flex align-items-center" style={{ gap: 10, padding: '20px 0' }}>
                      <Spinner size="sm" />
                      <span className="text-muted" style={{ fontSize: 13 }}>Carregando respostas…</span>
                    </div>
                  ) : resps.length === 0 ? (
                    <div
                      style={{
                        border: '1px solid #e9ecef',
                        borderRadius: 6,
                        background: '#f8f9fa',
                        padding: 20,
                        fontSize: 13,
                        color: '#6c757d',
                      }}
                    >
                      Nenhuma resposta coletada para este formulário ainda.
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                      <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
                        <thead style={{ background: '#f8f9fa' }}>
                          <tr>
                            <th style={{ width: 70 }}>ID</th>
                            <th>Preenchido por</th>
                            <th style={{ width: 100 }}>Versão</th>
                            <th style={{ width: 150 }}>Data</th>
                            <th style={{ width: 90 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {resps.map((r) => (
                            <tr key={r.projFormResp_id}>
                              <td>{r.projFormResp_id}</td>
                              <td>{r.pf_nome || (r.pf_id ? `PF #${r.pf_id}` : '—')}</td>
                              <td>{r.projFormResp_versaoForm || '—'}</td>
                              <td>{formatDateTime(r.projFormResp_dtCria)}</td>
                              <td className="text-right">
                                <Button color="primary" outline size="sm" onClick={() => openResp(r)}>
                                  Ver
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Modal criar / editar form */}
      <Modal isOpen={editorOpen} toggle={closeEditor} size="lg" centered>
        <ModalHeader toggle={closeEditor}>
          {isEditing ? `Editar formulário #${editingFormId}` : 'Novo formulário'}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Nome</Label>
            <Input
              value={editNome}
              onChange={(e) => setEditNome(e.target.value)}
              placeholder="Ex.: FOR 15 - Ficha de Coleta"
              disabled={editorSaving}
            />
          </FormGroup>
          <FormGroup>
            <Label>Versão</Label>
            <Input
              value={editVersao}
              onChange={(e) => setEditVersao(e.target.value)}
              placeholder="1"
              disabled={editorSaving}
              style={{ maxWidth: 120 }}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>Definição (JSON — seções)</Label>
            <Input
              type="textarea"
              rows={12}
              value={editDef}
              onChange={(e) => setEditDef(e.target.value)}
              disabled={editorSaving}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}
            />
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              Deve ser um array JSON no formato do FormTemplate (sections).
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline disabled={editorSaving} onClick={closeEditor}>
            Cancelar
          </Button>
          <Button color="primary" disabled={editorSaving} onClick={requestSaveForm}>
            {editorSaving ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Criar formulário'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmação: atualizar registro atual vs criar nova versão */}
      <Modal
        isOpen={saveChoiceOpen}
        toggle={() => !editorSaving && setSaveChoiceOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !editorSaving && setSaveChoiceOpen(false)}>
          Como deseja salvar?
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 14, marginBottom: 12 }}>
            Você está editando <strong>{editingOriginal?.projForm_nome || 'este formulário'}</strong>
            {editingOriginal?.projForm_versao ? (
              <>
                {' '}
                (versão atual <strong>{editingOriginal.projForm_versao}</strong>)
              </>
            ) : null}
            .
          </p>
          <div
            style={{
              border: '1px solid #e9ecef',
              borderRadius: 6,
              padding: 12,
              marginBottom: 10,
              background: '#f8f9fa',
              fontSize: 13,
            }}
          >
            <strong>Atualizar a versão atual</strong>
            <div className="text-muted mt-1">
              Altera o registro #{editingFormId}. Respostas já coletadas continuam ligadas a este formulário;
              a definição muda para quem abrir daqui pra frente.
            </div>
          </div>
          <div
            style={{
              border: '1px solid #e9ecef',
              borderRadius: 6,
              padding: 12,
              background: '#f8f9fa',
              fontSize: 13,
            }}
          >
            <strong>Criar uma nova versão</strong>
            <div className="text-muted mt-1">
              Gera um novo formulário com a versão informada no campo
              {editVersao ? (
                <>
                  {' '}
                  (<strong>{editVersao}</strong>)
                </>
              ) : null}
              . O registro anterior permanece intacto para histórico e respostas antigas.
              {editingOriginal && String(editVersao || '') === String(editingOriginal.projForm_versao || '1') && (
                <div className="text-danger mt-2">
                  Altere o campo Versão antes de criar uma nova (não pode ser igual à atual).
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex flex-wrap" style={{ gap: 8 }}>
          <Button color="secondary" outline disabled={editorSaving} onClick={() => setSaveChoiceOpen(false)}>
            Voltar
          </Button>
          <Button
            color="secondary"
            disabled={editorSaving}
            onClick={() => handleSaveForm('update')}
          >
            {editorSaving ? 'Salvando…' : 'Atualizar versão atual'}
          </Button>
          <Button
            color="primary"
            disabled={
              editorSaving ||
              (editingOriginal &&
                String(editVersao || '') === String(editingOriginal.projForm_versao || '1'))
            }
            onClick={() => handleSaveForm('new_version')}
          >
            {editorSaving ? 'Salvando…' : 'Criar nova versão'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal ver resposta */}
      <Modal isOpen={respModalOpen} toggle={() => setRespModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setRespModalOpen(false)}>
          Resposta #{selectedResp?.projFormResp_id}
        </ModalHeader>
        <ModalBody>
          {selectedResp && (
            <>
              <div className="d-flex flex-wrap" style={{ gap: 16, marginBottom: 12, fontSize: 13 }}>
                <div>
                  <span className="text-muted">Por:</span>{' '}
                  <strong>{selectedResp.pf_nome || (selectedResp.pf_id ? `PF #${selectedResp.pf_id}` : '—')}</strong>
                </div>
                <div>
                  <span className="text-muted">Versão form:</span>{' '}
                  <strong>{selectedResp.projFormResp_versaoForm || '—'}</strong>
                </div>
                <div>
                  <span className="text-muted">Data:</span>{' '}
                  <strong>{formatDateTime(selectedResp.projFormResp_dtCria)}</strong>
                </div>
              </div>
              <pre
                style={{
                  margin: 0,
                  padding: 12,
                  borderRadius: 6,
                  border: '1px solid #e9ecef',
                  background: '#f8f9fa',
                  fontSize: 12,
                  maxHeight: 420,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {tryPrettyJson(selectedResp.projFormResp_dados)}
              </pre>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setRespModalOpen(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ProjDadosTab
